import { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import { GestureDetector } from "react-native-gesture-handler";
import { useSharedValue, withSpring } from "react-native-reanimated";
import isTestUser from "src/constants/isTestUser";
import { paddingLarge } from "src/styles/spacing";
import InfiniteCarouselItem from "./components/InfiniteCarouselItem";
import useInfiniteCarouselGesture from "./hooks/useInfiniteCarouselGesture";
import { InfiniteCarouselProps } from "./types";

const defaultItemDim = {
	width: 100,
	height: 100,
};
const defaultItemSpacing = paddingLarge;
const InfiniteCarousel = <TItem extends any>({
	data,
	renderItem,
	itemDim = defaultItemDim,
	containerStyle,
	numItems = 5,
	overrideRenderedNumItems,
	itemSpacing = defaultItemSpacing,
	curveFactor = 0.05,
	scaleFactor = 0.2,
	opacityFactor = 0.5,
	autoPlay = true,
	onItemFocused,
	animatedStyleWorklet,
}: InfiniteCarouselProps<TItem> & {
	data: TItem[];
}) => {
	const renderedNumItems = useMemo(
		() => overrideRenderedNumItems ?? 2 * numItems,
		[numItems, overrideRenderedNumItems],
	);
	const [focusedIndex, setFocusedIndex] = useState(0);

	const animatedIndex = useSharedValue(0);
	const { gesture, isMoving } = useInfiniteCarouselGesture({
		animatedIndex,
		setFocusedIndex,
		itemWidth: itemDim.width,
		itemSpacing,
		numItems: renderedNumItems,
	});
	useEffect(() => {
		if (onItemFocused) {
			const item = data[focusedIndex % data.length];
			if (item) {
				onItemFocused({
					item,
					index: focusedIndex,
				});
			}
		}
	}, [focusedIndex, data, onItemFocused]);

	useEffect(() => {
		data.length;
		if (autoPlay && !isMoving) {
			const interval = setInterval(() => {
				const nextValue = Math.floor(animatedIndex.value + 1);
				setFocusedIndex(nextValue);
				animatedIndex.value = withSpring(nextValue, {
					damping: 50,
					restDisplacementThreshold: 0.001,
				});
			}, 3000);
			return () => clearInterval(interval);
		}
		return;
	}, [animatedIndex, autoPlay, data.length, isMoving]);

	const renderedData = useMemo(() => {
		const renderedData: { item: TItem; index: number }[] = [];
		const halfNumItems = Math.ceil(renderedNumItems / 2);
		for (let i = focusedIndex - halfNumItems + 1; i < focusedIndex + halfNumItems; i++) {
			// Using at for negative indexing
			const item = data.at(i % data.length);
			if (item) {
				renderedData.push({
					item,
					index: i,
				});
			}
		}
		return renderedData;
	}, [data, focusedIndex, renderedNumItems]);

	return (
		<GestureDetector gesture={gesture}>
			<View
				style={[
					{
						width: "100%",
						alignItems: "center",
						justifyContent: "center",
						height: itemDim.height,
					},
					containerStyle,
				]}
			>
				{renderedData.map(({ item, index }) => {
					return (
						<InfiniteCarouselItem
							key={index}
							item={item}
							index={index}
							renderItem={renderItem}
							itemDim={itemDim}
							animatedIndex={animatedIndex}
							itemSpacing={itemSpacing}
							isFocused={focusedIndex === index}
							curveFactor={curveFactor}
							scaleFactor={scaleFactor}
							opacityFactor={opacityFactor}
							animatedStyleWorklet={animatedStyleWorklet}
						/>
					);
				})}
			</View>
		</GestureDetector>
	);
};

/**
 * Version of InfiniteCarousel for test users that doesn't have weird looping
 */
const InfiniteCarouselTestComponent = <TItem extends any>({
	data,
	itemDim = defaultItemDim,
	containerStyle,
	itemSpacing = defaultItemSpacing,
	renderItem,
}: InfiniteCarouselProps<TItem> & {
	data: TItem[];
}) => {
	return (
		<View
			style={[
				{
					width: "100%",
					alignItems: "center",
					justifyContent: "center",
					height: itemDim.height,
					flexDirection: "row",
					overflow: "scroll",
				},
				containerStyle,
			]}
		>
			<View
				style={{
					flexDirection: "row",
					flex: 1,
					gap: itemSpacing,
				}}
			>
				{data.map((item, index) => {
					return (
						<View
							key={index}
							style={{
								width: itemDim.width,
								height: itemDim.height,
							}}
						>
							{renderItem({
								width: itemDim.width,
								height: itemDim.height,
								item,
								isFocused: false,
							})}
						</View>
					);
				})}
			</View>
		</View>
	);
};

export default (isTestUser
	? InfiniteCarouselTestComponent
	: InfiniteCarousel) as typeof InfiniteCarousel;
